import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import useApi from "../hooks/useApi";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

const Contact = () => {
  const { publicAuth } = useApi();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        element.classList.add("contact-animate-in");
        observer.unobserve(element);
      });
    }, observerOptions);

    document.querySelectorAll(".contact-animate").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required.";
    else if (!/^[A-Za-z][A-Za-z\s.'-]*$/.test(form.name.trim())) {
      nextErrors.name = "Enter a valid name.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!/^\d{10}$/.test(form.phone.trim())) {
      nextErrors.phone = "Enter a valid 10-digit phone number.";
    }
    if (!form.message.trim()) nextErrors.message = "Message is required.";
    else if (form.message.trim().length < 5) {
      nextErrors.message = "Message should be at least 5 characters.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      await publicAuth("contact-enquiries", "POST", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setForm(initialForm);
      swal("Submitted", "Your enquiry has been sent successfully.", "success");
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors) {
        setErrors({
          name: apiErrors.name?.[0] || "",
          email: apiErrors.email?.[0] || "",
          phone: apiErrors.phone?.[0] || "",
          message: apiErrors.message?.[0] || "",
        });
      }
      swal("Error", error?.response?.data?.message || "Unable to submit enquiry right now.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .contact-animate {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.55s ease, transform 0.55s ease;
        }
        .contact-animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .contact-page-wrap {
          background: #f6f8fb;
          padding: 90px 0;
        }
        .contact-panel {
          background: #fff;
          border: 1px solid #e8edf4;
          border-radius: 8px;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }
        .contact-info-panel {
          min-height: 100%;
          padding: 42px;
          background: linear-gradient(140deg, #123b75 0%, #1f6fb4 100%);
          color: #fff;
        }
        .contact-info-panel h3 {
          color: #fff;
          font-size: 30px;
          margin-bottom: 12px;
        }
        .contact-info-panel p,
        .contact-info-panel a {
          color: rgba(255, 255, 255, 0.88);
        }
        .contact-info-item {
          display: flex;
          gap: 16px;
          padding: 20px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.18);
        }
        .contact-info-icon {
          flex: 0 0 42px;
          width: 42px;
          height: 42px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.14);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .contact-form-panel {
          padding: 42px;
        }
        .contact-form-panel h2 {
          font-size: 34px;
          margin-bottom: 10px;
          color: #152238;
        }
        .contact-form-panel .form-control {
          min-height: 52px;
          border: 1px solid #dce3ed;
          border-radius: 6px;
          padding: 12px 14px;
          box-shadow: none;
        }
        .contact-form-panel textarea.form-control {
          min-height: 150px;
          resize: vertical;
        }
        .contact-form-panel .form-control:focus {
          border-color: #1f6fb4;
          box-shadow: 0 0 0 0.16rem rgba(31, 111, 180, 0.16);
        }
        .contact-error {
          color: #c62828;
          font-size: 13px;
          margin-top: 6px;
        }
        .contact-map-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
          font-weight: 600;
          text-decoration: underline;
        }
        @media (max-width: 991px) {
          .contact-page-wrap {
            padding: 54px 0;
          }
          .contact-info-panel,
          .contact-form-panel {
            padding: 28px;
          }
        }
      `}</style>

      <section className="page-title contact-animate">
        <div
          className="bg-layer"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/background/page-title.jpg)` }}
        ></div>
        <div className="auto-container">
          <div className="content-box">
            <h1>Contact Us</h1>
          </div>
        </div>
      </section>

      <section className="contact-page-wrap">
        <div className="auto-container">
          <div className="contact-panel contact-animate">
            <div className="row g-0">
              <div className="col-lg-5">
                <div className="contact-info-panel">
                  <h3>Talk to Asysta</h3>
                  <p>
                    Have a question about courses, driving schools, services, or bookings?
                    Send us a message and our team will get back to you.
                  </p>

                  <div className="contact-info-item">
                    <span className="contact-info-icon"><i className="fas fa-map-marker-alt"></i></span>
                    <div>
                      <h5 className="text-white mb-1">Address</h5>
                      <p className="mb-0">
                        <strong>Asysta India Private Limited</strong><br />
                        114 Street-5, Shastri Colony, Sonipat, Haryana
                      </p>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <span className="contact-info-icon"><i className="fas fa-envelope"></i></span>
                    <div>
                      <h5 className="text-white mb-1">Email</h5>
                      <p className="mb-0">
                        <a href="mailto:info@asysta.in">info@asysta.in</a>
                      </p>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <span className="contact-info-icon"><i className="fas fa-clock"></i></span>
                    <div>
                      <h5 className="text-white mb-1">Office Hours</h5>
                      <p className="mb-0">Monday - Saturday: 9:00 AM to 6:30 PM<br />Sunday: Holiday</p>
                    </div>
                  </div>

                  <a
                    className="contact-map-link"
                    href="https://www.google.com/maps/search/?api=1&query=114%20Street-5%2C%20Shastri%20Colony%2C%20Sonipat%2C%20Haryana"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View address on map <i className="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="contact-form-panel">
                  <h2>Send Enquiry</h2>
                  <p className="text-muted mb-4">
                    Fill in the details below. Your enquiry will be shared with the admin team.
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row clearfix">
                      <div className="col-md-6 form-group mb-3">
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="Your Name"
                          value={form.name}
                          onChange={handleChange}
                          disabled={submitting}
                        />
                        {errors.name && <div className="contact-error">{errors.name}</div>}
                      </div>

                      <div className="col-md-6 form-group mb-3">
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={form.phone}
                          onChange={handleChange}
                          maxLength={10}
                          disabled={submitting}
                        />
                        {errors.phone && <div className="contact-error">{errors.phone}</div>}
                      </div>

                      <div className="col-12 form-group mb-3">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address"
                          value={form.email}
                          onChange={handleChange}
                          disabled={submitting}
                        />
                        {errors.email && <div className="contact-error">{errors.email}</div>}
                      </div>

                      <div className="col-12 form-group mb-4">
                        <textarea
                          name="message"
                          className="form-control"
                          placeholder="Message"
                          value={form.message}
                          onChange={handleChange}
                          disabled={submitting}
                        ></textarea>
                        {errors.message && <div className="contact-error">{errors.message}</div>}
                      </div>

                      <div className="col-12">
                        <button type="submit" className="theme-btn btn-one border-0" disabled={submitting}>
                          {submitting ? "Sending..." : "Send Enquiry"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
